import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { AppConstant, DeviceName } from '@app/core/constant/app-constant';
import { ContentType, Path } from '@app/core/enums';
import { ProfileModel, User } from '@app/pages/shared/interfaces/user.model';
import {
  SearchModel,
  SearchResponseData,
} from '@app/pages/shared/interfaces/movie.model';
import { AuthService } from '@app/core/services/auth.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { ToasterService } from '@app/core/services/toaster.service';
import { UploadFilmService } from '@app/pages/shared/services/upload-film.service';
import { ViewerProfileService } from '@app/pages/shared/services/viewer-profile.service';
import { WindowRefService } from '@app/pages/shared/services/windowref.service';
import { environment } from '@environments/environment';
import { getMessaging, getToken, onMessage } from '@firebase/messaging';
import { DeviceDetectorService } from 'ngx-device-detector';
import { of, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { NotificationModel } from './../../../core/models/notification.model';
import { NotificationService } from './../../../core/services/notification.service';
import { BigShortService } from '@app/public/shared/services/big-short.service';
import { ModalService } from '@app/core/services/modal.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CookieService } from '@app/core/services/cookie.service';
import { Analytics } from '@app/core/services/analytics/analytics.service';
import { EventType } from '@app/core/enums/meta.enum';

import { WalletService } from '@app/pages/shared/services/wallet.service';
import { Pagination } from '@app/core/models/api-response.model';
import { PwaService } from '@app/core/services/pwa.service';
import { ValentineThemeService } from '@app/core/services/valentine-theme.service';
import { HoliThemeService } from '@app/core/services/holi-theme.service';
import { Settings } from '@app/core/services/settings.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() logout = new EventEmitter<void>();
  @ViewChild('movieSearchInput', { static: true }) movieSearchInput: ElementRef;
  @ViewChild('shareReferral') private readonly templateRef: TemplateRef<void>;
  modalRef: BsModalRef;
  cdnUrl = environment.cdnUrl;
  path = Path;
  isLoggedIn: boolean;
  user: User | undefined;
  isMobileMenuOpen = false;
  isSearchOpen = false;
  isValentineActive = false;
  isHoliActive = false;
  // New States
  isLanguageOpen = false;
  // isRewardsOpen removed - direct navigation used
  isNewUser = false;
  selectedLanguage = 'en'; // Default
  languageList: { code: string; name: string; id: string }[] = [];

  // Wallet & Coins
  walletBalance: number = 0;
  coinBalance: number = 0;
  currencySymbol: string = '\u20B9'; // Indian Rupee Symbol

  get profileImage(): string {
    return (
      this.userProfile?.profilePicUrl || 'assets/images/abc-user-avtar.svg'
    );
  }
  isScrolled: boolean = Boolean(false);
  search: string;
  errorMessage: string;
  isSearch: boolean = Boolean(true);
  focus: boolean = Boolean(false);
  userProfile: ProfileModel;
  switchRoleSubscription: Subscription;
  suggestionsSub: Subscription;
  noResult: boolean = Boolean(false);
  currentUrl: string;
  unSubscribeAll: Subject<void> = new Subject<void>();
  queryField: FormControl = new FormControl();
  matches: SearchResponseData | null = null;
  loadMoreData: SearchModel[] | null = null;
  isMatches: boolean;
  listItemHovered = false;
  isMobile: boolean = Boolean(false);
  isAppNotification = Boolean(true);
  profileIcon: string;
  notificationIcon: string;
  isNotification: boolean = Boolean(false);
  isProfile: boolean = Boolean(false);
  isNotificationPush: boolean;
  message: any;
  redirectionUrl: string;
  fcmToken: string;
  notificationList: NotificationModel[] = [];
  isNotificationSend: boolean = Boolean(false);
  profileRole: string;
  filmfactoryStaticLink: string = environment.filmfactory_static_link;
  @ViewChild('myDiv') myDiv: ElementRef<HTMLElement>;
  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.isMobile = this.winRef.isMobile();
  }
  isLoading = false;
  visitorId: string = '';
  readonly bigShortURL = AppConstant.StaticUrl.bigShortUrl;

  // Pagination state for search
  currentSearchPage = 1;
  hasMoreResults = false;
  isLoadingMore = false;
  currentSearchLimit = 10;
  currentSearchQuery = '';
  filmSkipNumber: number = 0;
  webSeriesSkipNumber: number = 0;
  microSeriesSkipNumber: number = 0;
  filmHasMore: boolean = false;
  webSeriesHasMore: boolean = false;
  microSeriesHasMore: boolean = false;

  // Close dropdowns on outside click - Comprehensive Logic
  @HostListener('document:click', ['$event']) onClick(event: any) {
    const target = event.target;

    // 1. Close Notification if clicked outside '.nav-item-wrapper' (that isn't specific enough, need to rely on the button/dropdown)
    // Actually, Notification has class 'nav-item-wrapper' but no specific identifier.
    // Let's assume the first .nav-item-wrapper is notification? No.
    // The HTML has structure. Let's look at specific classes.
    // Notification wrapper doesn't have a unique class besides 'nav-item-wrapper'.
    // BUT Profile has 'profile-wrapper'. Language has 'language-wrapper'.
    // So 'nav-item-wrapper' MINUS those two ==> Notification wrapper.

    // Better Logic: Check if click is inside the specific DROPDOWN or BUTTON.

    // Close Notification
    if (
      this.isNotification &&
      !target.closest('.abc-notification-dropdown') &&
      !target.closest('#abc-notification')
    ) {
      this.isNotification = false;
    }

    // Close Profile
    if (
      this.isProfile &&
      !target.closest('.abc-profile-dropdown') &&
      !target.closest('.profile-menu')
    ) {
      this.isProfile = false;
    }

    // Close Language
    if (
      this.isLanguageOpen &&
      !target.closest('.abc-language-dropdown') &&
      !target.closest('.lang-btn') &&
      !target.closest('.language-modal')
    ) {
      this.isLanguageOpen = false;
    }

    // Close Mobile Menu if clicked outside
    if (
      this.isMobile &&
      this.isMobileMenuOpen &&
      !target.closest('.mobile-nav') &&
      !target.closest('.hamburger') &&
      !target.closest('.nav-item')
    ) {
      this.isMobileMenuOpen = false;
    }

    const elementId = target.id;
    if (elementId !== 'film-search' && elementId !== 'film-search-dropdown') {
      this.focus = false;
    } else {
      this.focus = true;
    }
  }

  public readonly PAGE = 'Navigation bar';

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    public toaster: ToasterService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly filmService: UploadFilmService,
    public profileService: ViewerProfileService,
    private readonly winRef: WindowRefService,
    private readonly notificationService: NotificationService,
    private readonly deviceService: DeviceDetectorService,
    public localStorageService: LocalStorageService,
    protected bigShortService: BigShortService,
    private readonly modalService: ModalService,
    private readonly cookieService: CookieService,
    private readonly analytics: Analytics,
    private readonly walletService: WalletService,
    public readonly pwaService: PwaService, // Public for template access
    private readonly valentineService: ValentineThemeService,
    private readonly holiService: HoliThemeService,
    private readonly settingsService: Settings,
  ) {
    this.isLoggedIn = this.authService.isLoggedIn.value;

    this.valentineService.isActive$
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((isActive: boolean) => {
        this.isValentineActive = isActive;
        this.changeDetectorRef.markForCheck();
      });

    this.holiService.isActive$
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((isActive: boolean) => {
        this.isHoliActive = isActive;
        this.changeDetectorRef.markForCheck();
      });
  }

  installPwa(): void {
    this.pwaService.installPwa();
  }

  getScreenSize(): void {
    this.isMobile = this.winRef.isMobile();
  }

  private handleRouterEvents(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = this.router.url;
      }
    });
  }

  get displayPrimary(): string {
    const firstName = this.userProfile?.firstname || this.user?.firstName;
    const lastName = this.userProfile?.lastname || this.user?.lastName;

    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }

    const mobile = this.userMobile;
    if (mobile) return mobile;

    return this.userProfile?.email || this.user?.email || '';
  }

  get displaySecondary(): string {
    const firstName = this.userProfile?.firstname || this.user?.firstName;
    const lastName = this.userProfile?.lastname || this.user?.lastName;
    const hasName = !!(firstName || lastName);

    const mobile = this.userMobile;
    const email = this.userProfile?.email || this.user?.email;

    if (hasName) {
      // If we showed name, show Mobile -> Email
      if (mobile) return mobile;
      return email || '';
    }

    // If we showed Mobile as primary, show Email
    if (mobile) {
      return email || '';
    }

    // If we showed Email as primary, show nothing
    return '';
  }

  get userMobile(): string {
    // Check ProfileModel (PhoneNumberModel object or related fields)
    if (this.userProfile) {
      const phoneObj = this.userProfile.phoneNumber as any;

      // 1. Try internationalNumber from PhoneNumberModel (e.g. "+91 6363637777")
      if (phoneObj?.internationalNumber) {
        return phoneObj.internationalNumber;
      }

      // 2. Try constructing from PhoneNumberModel dialCode + number
      if (phoneObj?.dialCode && phoneObj?.number) {
        return `${phoneObj.dialCode} ${phoneObj.number}`;
      }

      // 3. Try constructing from root countryCodeNumeric + phoneNumber (User Request Scenario)
      // Sometimes APIs return countryCodeNumeric at root level and number inside or as root string
      if (this.userProfile.countryCodeNumeric && phoneObj?.number) {
        return `${this.userProfile.countryCodeNumeric} ${phoneObj.number}`;
      }
      // Fallback: If phoneNumber is just a string, try prepending countryCodeNumeric
      if (
        typeof this.userProfile.phoneNumber === 'string' &&
        this.userProfile.countryCodeNumeric
      ) {
        return `${this.userProfile.countryCodeNumeric} ${this.userProfile.phoneNumber}`;
      }

      // 4. Fallback to just number if nothing else
      if (phoneObj?.number) {
        return phoneObj.number;
      }
      if (typeof this.userProfile.phoneNumber === 'string') {
        return this.userProfile.phoneNumber;
      }
    }

    // Check User object (legacy/fallback)
    if (this.user?.phoneNumber) {
      return this.user.phoneNumber;
    }
    return '';
  }

  ngOnInit(): void {
    this.handleRouterEvents();

    // Initialize Language from LocalStorage
    const savedLang = this.localStorageService.get('appLanguage');
    if (savedLang) {
      this.selectedLanguage = savedLang;
    }

    // Fetch app-enabled languages with their IDs
    this.settingsService
      .getAppEnabledLanguages()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.languageList = (response.data || []).map((lang: any) => ({
            code: lang.default,
            name: lang.name,
            id: lang._id,
          }));
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          // Fallback to static list if API fails
          this.languageList = [
            { code: 'en', name: 'English', id: '' },
            { code: 'ml', name: 'Malayalam', id: '' },
            { code: 'ta', name: 'Tamil', id: '' },
            { code: 'kn', name: 'Kannada', id: '' },
          ];
        },
      });

    if (!this.bigShortService.isInitialized())
      this.bigShortService.initializeBigShortAndStaticBannerData(this.PAGE);

    if (
      this.deviceService.os !== DeviceName.iOS &&
      this.deviceService.os !== DeviceName.mac
    ) {
      this.listen();
    }
    this.getScreenSize();
    this.onSearch();
    this.authService.userSubject
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((value) => {
        if (value) {
          this.user = value;
          this.changeDetectorRef.markForCheck();
        }
      });

    this.authService.isLoggedIn
      .pipe(distinctUntilChanged(), takeUntil(this.unSubscribeAll))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        this.changeDetectorRef.markForCheck();
        if (isLoggedIn) {
          // Use getProfileIfNeeded() — fetches HTTP only if the profile cache
          // is empty (i.e. first load or after logout). If the profile page
          // already populated the cache, this returns immediately with no HTTP call.
          this.profileService
            .getProfileIfNeeded()
            .pipe(takeUntil(this.unSubscribeAll))
            .subscribe();
          this.getNotificationList();
          this.getWalletBalance();
          // Listen for Wallet Changes
          this.walletService.currentTransaction
            .pipe(takeUntil(this.unSubscribeAll))
            .subscribe((res) => {
              if (res > 0) this.getWalletBalance();
            });
        }
      });

    // this.checkNewUserLanguage(); // Removed

    // Subscribe to real-time profile updates.
    // This is the SINGLE place the navbar reacts to profile data —
    // no separate getProfile() HTTP call needed.
    this.profileService.profile$
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((profile) => {
        if (profile) {
          this.userProfile = profile;
          this.profileRole = profile.role;
          this.changeDetectorRef.markForCheck();

          // Force-logout check
          if (profile.isForceLogout) {
            if (profile.role !== 'FILM_MAKER') {
              this.onClickLogout();
            } else {
              this.authService._saveUser(profile as any);
            }
          } else if (
            this.router.url.includes('app/filmmaker') &&
            this.profileRole === 'VIEWER'
          ) {
            this.router.navigate([Path.Home]);
          } else if (
            this.router.url === '/' &&
            this.profileRole === 'FILM_MAKER'
          ) {
            this.router.navigate(['app/filmmaker/dashboard']);
          }
        }
      });

    // Subscribe to real-time notification updates
    this.notificationService.notifications$
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((notifications) => {
        this.notificationList = notifications;
        const readNotificationList = this.notificationList.filter(
          (n) => n.read === false,
        );
        this.isNotificationPush = readNotificationList.length > 0;
        this.changeDetectorRef.markForCheck();
      });

    this.changeDetectorRef.detectChanges();
    window.addEventListener('scroll', () => {
      if (window.scrollY > 0) {
        this.isScrolled = true;
      } else {
        this.isScrolled = false;
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleKeyboardEvent(event: Event) {
    if (this.isSearchOpen) {
      this.closeAbcSearch();
    }
  }

  ngAfterViewInit(): void {}

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  onSearch(): void {
    this.queryField.valueChanges
      .pipe(
        tap((value: string) => {
          if (value && value.trim().length > 2) {
            this.isLoading = true;
            // Reset pagination on new search
            this.currentSearchPage = 1;
            this.hasMoreResults = false;
          } else {
            this.isLoading = false;
            this.matches = null;
            this.isMatches = false;
            this.currentSearchPage = 1;
            this.hasMoreResults = false;
            this.currentSearchQuery = '';
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((query: string) => {
          if (query && query.trim().length > 2) {
            this.currentSearchQuery = query.trim();
            return this.filmService
              .searchFilm(query.trim(), 1, this.currentSearchLimit, false)
              .pipe(
                map((res) => {
                  this.analytics.customAnalytics('Search Film Success', {
                    eventType: EventType.api,
                    page: this.PAGE,
                    keyword: this.search,
                  });
                  const { pagination } = res;
                  this.filmHasMore = pagination?.filmHasMore || false;
                  this.webSeriesHasMore = pagination?.webSeriesHasMore || false;
                  this.microSeriesHasMore =
                    pagination?.microSeriesHasMore || false;
                  this.hasMoreResults = pagination?.hasMore || false;
                  this.filmSkipNumber = pagination?.filmSkipNumber || 0;
                  this.webSeriesSkipNumber =
                    pagination?.webSeriesSkipNumber || 0;
                  this.microSeriesSkipNumber =
                    pagination?.microSeriesSkipNumber || 0;
                  return res?.data || {};
                }),
                tap({
                  error: (error) => {
                    console.error('❌ [Navbar Search] API error:', error);
                    this.errorMessage =
                      error.error?.message ??
                      error?.message ??
                      'Something goes wrong';
                    this.analytics.failureEvent('Search Film Failure', {
                      error: '/film/search API Error',
                      page: this.PAGE,
                      keyword: this.search,
                      reason: this.errorMessage,
                    });
                  },
                }),
              );
          }
          this.isLoading = false;
          return of({});
        }),
      )
      .subscribe((resp: SearchResponseData) => {
        this.matches = {
          language: resp.language?._id ? resp.language : undefined,
          genre: resp.genre?._id ? resp.genre : undefined,
          filmMaker: resp.filmMaker?._id ? resp.filmMaker : undefined,
          films: resp.films,
          webSeries: resp.webSeries,
          microSeries: resp.microSeries,
        };

        this.loadMoreData = [];
        // Check if we have any results — empty objects {} are falsy for _id check
        this.isMatches = !!(
          resp?.genre?._id ||
          resp?.language?._id ||
          resp?.filmMaker?._id ||
          resp?.films?.length ||
          resp?.microSeries?.length ||
          resp?.webSeries?.length
        );

        // Check if there are more results (if we got 10 items total, there might be more)
        const totalItems =
          (resp?.films?.length || 0) +
          (resp?.microSeries?.length || 0) +
          (resp?.webSeries?.length || 0) +
          (resp?.genre ? 1 : 0) +
          (resp?.language ? 1 : 0) +
          (resp?.filmMaker ? 1 : 0);

        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  getNotificationList(): void {
    this.notificationService
      .getNotificationList()
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe({
        next: (res) => {
          // Service takes care of updating the subject, but we can set initial list here if needed
          // The subscription in ngOnInit will handle the view update
        },
        error: (error) => {
          this.analytics.failureEvent('Get Notification List Failure', {
            error: 'user/notification/list API Error',
            page: this.PAGE,
            reason: error.error?.message ?? error?.message ?? '',
          });
        },
      });
  }

  getProfile(): void {
    this.profileService
      .getProfile()
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe({
        next: (res: any) => {
          this.userProfile = res.data;
          this.profileRole = this.userProfile.role;

          if (this.userProfile.isForceLogout) {
            if (this.userProfile.role !== 'FILM_MAKER') {
              this.onClickLogout();
            } else {
              this.authService._saveUser(res.data);
            }
          } else if (
            this.router.url.includes('app/filmmaker') &&
            this.profileRole == 'VIEWER'
          ) {
            this.router.navigate([Path.Home]);
          } else if (
            this.router.url == '/' &&
            this.profileRole == 'FILM_MAKER'
          ) {
            this.router.navigate(['app/filmmaker/dashboard']);
          }

          // if (
          //   !this.userProfile.webFcmToken &&
          //   this.deviceService.os !== DeviceName.iOS &&
          //   this.deviceService.os !== DeviceName.mac
          // ) {
          //   this.requestPermission();
          // }
        },
        error: (error) => {
          this.analytics.failureEvent('Get Profile Failure', {
            error: '/user/profile API Error',
            page: this.PAGE,
            reason: error.error?.message ?? error?.message,
          });
        },
      });
  }

  getWalletBalance(): void {
    this.walletService
      .getBalance()
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe({
        next: (res) => {
          const isWithdrawable = res.data.isWithdrawable;
          const promotionalCash = res.data.rewardAmount.rewardAmount;
          const rechargeAmount = res.data.netAmount.netAmount;

          // Show Recharge Balance only as per user request
          this.walletBalance = rechargeAmount;

          this.coinBalance = res.data?.coinAmount?.coinAmount || 0;
          this.currencySymbol = '\u20B9';
          this.changeDetectorRef.markForCheck();
        },
        error: (error: any) => {
          console.error('Failed to load wallet balance', error);
        },
      });
  }

  sendFCMToken(): void {
    const param = {
      email: this.userProfile.email,
      phoneNumber: this.userProfile.phoneNumber,
      webFcmToken: this.fcmToken,
    };
    this.profileService
      .sendFCMToken(param)
      .pipe(take(1))
      .subscribe((res) => {});
  }

  onClickLogout(): void {
    this.profileService.clearProfile();
    this.authService.logout();
    this.analytics.customAnalytics('Force Logout Success', {
      page: 'Navbar',
      eventType: EventType.success,
    });
    this.router.navigate([Path.SignIn]);
  }

  openAbcMenu(): void {
    document.body.classList.add('abc-menu-open');
  }

  closeAbcMenu(): void {
    document.body.classList.remove('abc-menu-open');
  }

  openAbcSearch(): void {
    this.isSearchOpen = true;
    setTimeout(() => {
      const input = document.getElementById('abc-search-input');
      if (input) input.focus();
    }, 100);
    document.body.classList.add('abc-search-open');
  }

  closeAbcSearch(mediaType?: string, id?: string): void {
    if (mediaType && id) {
      mediaType = mediaType.toLowerCase();
      const path =
        mediaType === ContentType.FILM ? Path.MOVIE_DETAIL : Path.WEB_SERIES;
      this.router.navigate([Path.App, path, id], {
        queryParams: { type: mediaType },
      });
    }

    this.isSearchOpen = false;
    this.search = '';
    this.queryField.setValue(''); // Clear the form control
    this.isMatches = false;
    document.body.classList.remove('abc-search-open');
  }

  clearSearch(): void {
    this.search = '';
    this.queryField.setValue(''); // Clear the form control
    this.isMatches = false;
    // Keep focus on input
    setTimeout(() => {
      const input = document.getElementById('abc-search-input');
      if (input) input.focus();
    }, 50);
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  openSubmenu(el: any): void {
    el.target.parentElement.classList.toggle('open');
  }

  closeSubmenu(): void {
    const dropdownItems = document.querySelectorAll(
      '.abc-header .abc-has-submenu',
    );
    dropdownItems.forEach((dropdownItem) => {
      dropdownItem.classList.remove('open');
    });
  }

  sendNotificationInProfile(): void {
    this.profileService
      .sendNotificationInfo(this.isNotificationSend)
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe((response) => {});
  }

  switchRole(role: string): void {
    if (role === 'FILM_MAKER') {
      window.open(environment.studioUrl, '_blank');
    } else {
      this.router.navigate([Path.Home]);
    }
    // this.profileRole = role;
    // this.isNotificationSend = this.userProfile?.isNotificationSend;
    // this.switchRoleSubscription = this.authService
    //   .switchUserRole(role, this.isNotificationSend)
    //   .pipe(takeUntil(this.unSubscribeAll))
    //   .subscribe({
    //     next: (res) => {
    //       if (res.data && res.token) {
    //         this.authService._saveToken(res.token);
    //         this.authService._saveUser(res.data);
    //         this.sendNotificationInProfile();
    //         if (role === 'FILM_MAKER') {
    //           this.router.navigate(['app/filmmaker/dashboard']);
    //         } else if (role === 'VIEWER') {
    //           this.router.navigate(['/']);
    //         }
    //         this.toaster.success(res.message);
    //         this.analytics.customAnalytics('Switch Role Success', {
    //           page: this.PAGE,
    //           eventType: EventType.success,
    //         });
    //       }
    //     },
    //     error: (error) => {
    //       if (error.status !== 403) {
    //         this.toaster.error(error?.error?.message ?? error.message);
    //       }
    //       this.analytics.failureEvent('Switch Role Failure', {
    //         error: '/switch-role API Error',
    //         page: this.PAGE,
    //         reason: error.error?.message ?? error?.message,
    //       });
    //     },
    //   });
  }

  checkForRoles(): boolean {
    return (
      this.userProfile?.otherRoles &&
      this.userProfile?.otherRoles?.indexOf('FILM_MAKER') !== -1
    );
  }

  routeToFilmMaker(path: string): void {
    this.analytics.customAnalytics('Redirecting to Filmmaker', {
      eventType: EventType.log,
      page: this.PAGE,
    });

    const isIndianUser =
      this.userProfile?.countryCode?.toLocaleLowerCase() ===
      AppConstant.IndianDetails.indianCountryCode.toLocaleLowerCase();

    const isVerified = isIndianUser
      ? this.userProfile.isPhoneVerified && this.userProfile.isEmailVerified
      : this.userProfile.isEmailVerified && this.userProfile.phoneNumber;
    let verificationMessage = '';

    if (isVerified) {
      this.router.navigate([path]);
      return;
    }

    if (isIndianUser) {
      if (
        !this.userProfile.isEmailVerified &&
        !this.userProfile.isPhoneVerified
      ) {
        verificationMessage = 'toaster.Please verify Email and Phone';
      } else if (!this.userProfile.isEmailVerified) {
        verificationMessage = 'toaster.Please verify Email';
      } else if (!this.userProfile.isPhoneVerified) {
        verificationMessage = 'toaster.Please verify Phone';
      }
    } else {
      verificationMessage = 'toaster.Please verify Email';
    }
    this.toaster.error(verificationMessage);
  }

  goToSearch(searchVal: any): void {
    this.router.navigate(['/app/search'], {
      queryParams: { key: searchVal },
    });
    this.closeAbcSearch();
  }

  hoverListItem(classList: DOMTokenList, isHover: boolean): void {
    isHover ? classList.add('active') : classList.remove('active');
  }

  closeDropDown(): void {
    this.focus = false;
  }

  onclick(): void {
    if (this.isNotification) {
      this.openNotification();
    }
    if (this.isProfile) {
      this.toggleProfile();
    }
  }

  closePopup(): void {
    this.isAppNotification = false;
    document.body.classList.remove('abc-app-notification-space');
  }

  openNotification(): void {
    this.isNotification = !this.isNotification;
    if (this.isNotification) {
      this.isProfile = false;
      this.isLanguageOpen = false;
      this.isSearchOpen = false;
    }
  }

  markAllRead(): void {
    this.isNotificationPush = false;
    this.notificationService
      .markAllAsRead(this.fcmToken)
      .pipe(takeUntil(this.unSubscribeAll))
      .subscribe({
        next: (res) => {
          this.getNotificationList();
          this.isNotification = true;
          this.openNotification();
          this.analytics.customAnalytics('Notification Mark All Read Success', {
            page: this.PAGE,
            eventType: EventType.api,
          });
        },
        error: (error) => {
          this.analytics.failureEvent('Notification Mark All Read Failure', {
            error: '/seen/all/notification API Error',
            page: this.PAGE,
            reason: error.error?.message ?? error?.message,
          });
        },
      });
  }

  // --- Language Logic ---
  openLanguage(): void {
    this.isLanguageOpen = true;
    this.isProfile = false;
    this.isNotification = false;
  }

  toggleLanguage(): void {
    this.isLanguageOpen = !this.isLanguageOpen;
    if (this.isLanguageOpen) {
      this.isProfile = false;
      this.isNotification = false;
    }
  }

  closeLanguage(): void {
    this.isLanguageOpen = false;
  }

  openRewards(): void {
    // Redirection as per production logic (no modal)
    this.router.navigate(['/app/profile/viewer'], {
      queryParams: { activeTab: 7 },
    });
  }

  selectLanguage(langCode: string): void {
    this.selectedLanguage = langCode;
    this.localStorageService.set('appLanguage', this.selectedLanguage);
    this.closeLanguage();

    if (this.isLoggedIn) {
      const langObj = this.languageList.find((l) => l.code === langCode);
      this.authService
        .setDefaultLanguage({
          defaultLanguage: langCode,
          defaultLanguageId: langObj?.id,
        })
        .pipe(take(1))
        .subscribe({
          next: () => window.location.reload(),
          error: () => window.location.reload(),
        });
    } else {
      window.location.reload();
    }
  }

  getLanguageName(code: string): string {
    return this.languageList.find((l) => l.code === code)?.name || code;
  }

  saveLanguage(): void {
    this.selectLanguage(this.selectedLanguage);
  }

  // checkNewUserLanguage removed as requested (no auto-popup)

  closeNotification(event: any): void {
    this.isNotification = false;
  }

  onRedirect(notification: any): void {
    this.notificationService
      .readNotification(notification._id, this.fcmToken)
      .subscribe((res) => {
        this.getNotificationList();
        this.setRedirectionUrl(notification);
      });
  }

  setRedirectionUrl(notification: NotificationModel): void {
    let url = notification.redirectURL;

    if (this.isDownloadNow(notification)) {
      url = this.getDownloadUrl();
      window.open(url, '_blank');
      return;
    }

    if (this.handleNavigation(notification)) return;

    if (notification.externalURL) {
      window.open(notification.externalURL, '_blank');
      return;
    }

    if (url.includes('?')) {
      this.handleQueryParamNavigation(url);
      return;
    }

    this.router.navigate([url]);
  }

  private isDownloadNow(notification: NotificationModel): boolean {
    return notification.title === 'Download Now';
  }

  private getDownloadUrl(): string {
    return this.deviceService.os === DeviceName.iOS ||
      this.deviceService.os === DeviceName.mac
      ? AppConstant.HomePageIconUrl.AppStoreLink
      : AppConstant.HomePageIconUrl.PlayStoreLink;
  }

  private handleNavigation(notification: NotificationModel): boolean {
    if (notification.filmId) {
      this.router.navigate(['/app/movie-detail/' + notification.filmId]);
      return true;
    }
    if (notification.genreId) {
      this.router.navigate(['view-film-list/genre'], {
        queryParams: { id: notification.genreId },
      });
      return true;
    }
    if (notification.languageId) {
      this.router.navigate(['/view-film-list/language'], {
        queryParams: { id: notification.languageId },
      });
      return true;
    }
    return false;
  }

  private handleQueryParamNavigation(url: string): void {
    if (this.currentUrl.includes('app/profile/viewer')) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/app/profile/viewer']);
      });
    }
    const [path, query] = url.split('?');
    this.router.navigate([path], { queryParams: { activeTab: query } });
  }

  toggleProfile(): void {
    this.isProfile = !this.isProfile;
    // Close other dropdowns
    if (this.isProfile) {
      this.isNotification = false;
      this.isLanguageOpen = false;
    }
  }

  closeProfile(): void {
    this.isProfile = false;
  }

  openReferralDialog(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template, {
      class: 'modal-dialog-centered modal-md abc-transparent-modal',
      backdrop: 'static',
    });
  }

  closeReferralDialog() {
    this.modalRef.hide();
  }

  requestPermission(): void {
    const messaging = getMessaging();

    getToken(messaging, {
      vapidKey: environment.firebase.vapidKey,
    })
      .then((currentToken) => {
        if (currentToken) {
          this.fcmToken = currentToken;
          this.localStorageService.set('FCMToken', this.fcmToken);

          if (
            this.fcmToken &&
            this.userProfile?.webFcmToken !== this.fcmToken
          ) {
            this.sendFCMToken();
          }

          if (this.fcmToken) {
            this.analytics.customAnalytics('FCM Token Success', {
              page: this.PAGE,
              eventType: EventType.api,
              message: 'FCM Token received',
            });
          } else {
            this.analytics.failureEvent('FCM Token Failure', {
              error: 'Token undefined',
              page: this.PAGE,
              reason: 'FCM Token does not generated.',
              eventType: EventType.error,
            });
          }
        }
      })
      .catch((error) => {
        this.analytics.failureEvent('FCM Token Failure', {
          error: 'getFCMToken API Error',
          page: this.PAGE,
          reason: error.error?.message ?? error?.message,
          eventType: EventType.error,
        });
        console.error('toaster.firebase error', error);
      });
  }

  listen(): void {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      if (this.user?.role === 'VIEWER') {
        this.message = payload;
        setTimeout(() => {
          this.getNotificationList();
          this.getWalletBalance(); // Refresh wallet on notification (e.g. cashback)
        }, 2000);
        this.isNotificationPush = true;
        const el: HTMLElement = this.myDiv.nativeElement;
        el.click();
        this.toaster.info(
          this.message.notification.body,
          this.message.notification.title,
          {
            timeOut: 12000,
            extendedTimeOut: 1000,
          },
        );
        if (this.message?.data) {
          this.redirectionUrl = this.message?.data['gcm.notification.url'];
        }
      }
    });
  }

  trackByMethod(index: number): number {
    return index;
  }

  getBadgeClass(mediaType?: string): string {
    mediaType = mediaType?.toLowerCase();
    switch (mediaType) {
      case 'film':
        return 'movie-badge';
      case 'web_series':
        return 'series-badge';
      case 'micro_series':
        return 'microseries-badge';
      default:
        return '';
    }
  }

  getBadgeText(mediaType?: string): string {
    mediaType = mediaType?.toLowerCase();
    switch (mediaType) {
      case 'film':
        return 'MOVIE';
      case 'web_series':
        return 'SERIES';
      case 'micro_series':
        return 'SHORTS';
      default:
        return '';
    }
  }

  ngOnDestroy(): void {
    if (this.switchRoleSubscription) {
      this.switchRoleSubscription.unsubscribe();
    }
    if (this.suggestionsSub) {
      this.suggestionsSub.unsubscribe();
    }
    if (this.unSubscribeAll) {
      this.unSubscribeAll.next();
      this.unSubscribeAll.complete();
    }
  }
}
